import React from 'react';
import { useIntl } from 'react-intl';
import { throttle } from 'throttle-debounce';
import Button from 'components/button';
import Html from 'components/html';

import tracking from './video-dreams-banner.tracking';
import { config, testId } from './video-dreams-banner.settings';
import messages from './video-dreams-banner.i18n';
import styles from './video-dreams-banner.module.scss';

interface VideoDreamsBannerElement {
  videoUrl: string;
  onSignUp: () => void;
}

const VideoDreamsBanner: React.FunctionComponent<VideoDreamsBannerElement> = (props) => {
  const { videoUrl, onSignUp } = props;
  const bannerRef = React.useRef<HTMLVideoElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const isLoading = React.useRef<boolean>(true);

  const { formatMessage } = useIntl();
  const translation = messages.pages.landing.videoDreamsBanner.livejasmin;

  React.useLayoutEffect(() => {
    const handleWindowScroll = throttle(
      200,
      async (): Promise<void> => {
        if (!bannerRef.current || !videoRef.current || isLoading.current) {
          return;
        }

        const isVideoVisible = videoRef.current.scrollHeight + config.scrollPauseBufferInPx > window.scrollY;
        const isVideoPaused = videoRef.current.paused;

        if (!isVideoVisible && !isVideoPaused) {
          videoRef.current.pause();
        } else if (isVideoVisible && isVideoPaused) {
          try {
            await videoRef.current.play();
          } catch (err) {
            console.error(err);
          }
        }
      },
      { noLeading: true }
    );

    window.addEventListener('scroll', handleWindowScroll);

    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [isLoading]);

  const handleVideoPlay = React.useCallback(() => {
    isLoading.current = false;
    bannerRef.current?.classList.remove('opacity-0');
  }, []);

  const handleVideoPause = React.useCallback(() => {
    isLoading.current = false;
    bannerRef.current?.classList.add('opacity-0');
  }, []);

  return (
    <Html.div className={styles.videoDreamsBanner} testId={testId.component} ref={bannerRef}>
      <Html.video
        className={styles.player}
        ref={videoRef}
        src={videoUrl}
        autoPlay
        loop
        muted
        playsInline
        onPlay={handleVideoPlay}
        onPause={handleVideoPause}
        onError={handleVideoPause}
        testId={testId.video}
      />
      <Html.div className={[styles.meta, 'px-4 pb-8 pb-md-0 ps-md-16']}>
        <Html.div className={[styles.rotation, 'mb-2 mb-md-1']}>
          {config.rotation.map((i18n) => (
            <Html.span key={i18n.id}>{formatMessage(i18n)}</Html.span>
          ))}
        </Html.div>
        <Html.div className={[styles.title, 'mb-2']}>{formatMessage(translation.title)}</Html.div>
        <Html.div className={[styles.join, 'mb-4 mb-md-8']}>{formatMessage(translation.join, { num: 1 })}</Html.div>
        <Button className="w-100 w-md-auto" onClick={onSignUp} testId={testId.signUp} tracking={tracking.joinNow}>
          {formatMessage(translation.signUp)}
        </Button>
      </Html.div>
    </Html.div>
  );
};

export type { VideoDreamsBannerElement };
export default VideoDreamsBanner;
